import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { useModerateTheme, usePendingReviewThemes } from '@/lib/themes/themes-queries';

export function TutorAdminThemesModerationPage() {
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const { data: themes = [], isLoading } = usePendingReviewThemes(isAdmin);
  const moderate = useModerateTheme();
  const [reasonByTheme, setReasonByTheme] = useState<Record<string, string>>({});

  if (!isAdmin) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <p className="text-ink-500 text-sm">Réservé aux admins.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-8">
      <header className="mb-5 flex flex-col gap-2">
        <h1 className="font-display text-ink-900 text-[24px] font-bold">Modération thèmes</h1>
        <p className="text-ink-500 font-sans text-[14px]">
          {themes.length} thème{themes.length > 1 ? 's' : ''} en attente
        </p>
      </header>

      {isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : themes.length === 0 ? (
        <p className="text-ink-500 text-sm">Aucun thème en attente.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {themes.map((t) => (
            <div
              key={t.id}
              className="border-ink-200 flex flex-col gap-3 rounded-2xl border bg-white p-4"
            >
              <div>
                <p className="font-display text-ink-900 text-[16px] font-bold">{t.title}</p>
                {t.short_description && (
                  <p className="text-ink-500 mt-1 font-sans text-sm">{t.short_description}</p>
                )}
                <p className="text-ink-400 mt-2 text-xs">
                  slug : <code className="font-mono">{t.slug}</code> · niveau{' '}
                  {t.min_level ?? 'tous'} · sensibilité {t.sensitivity}
                </p>
              </div>
              {t.description_md && (
                <details className="text-ink-600 text-sm">
                  <summary className="cursor-pointer font-semibold">Description longue</summary>
                  <pre className="mt-2 whitespace-pre-wrap rounded-xl bg-gray-50 p-3 text-xs">
                    {t.description_md}
                  </pre>
                </details>
              )}

              <input
                type="text"
                placeholder="Raison (optionnelle, transmise à l'auteur en cas de refus)"
                value={reasonByTheme[t.id] ?? ''}
                onChange={(e) => setReasonByTheme((m) => ({ ...m, [t.id]: e.target.value }))}
                className="border-ink-200 focus:border-primary-500 min-h-[2.75rem] w-full rounded-xl border bg-white px-4 py-2 text-sm outline-none"
              />

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  onClick={() =>
                    moderate.mutate({
                      themeId: t.id,
                      action: 'reject',
                      reason: reasonByTheme[t.id] || undefined,
                    })
                  }
                  disabled={moderate.isPending}
                  className="text-ink-700 border-ink-200 inline-flex min-h-[2.75rem] items-center justify-center gap-1 rounded-xl border px-4 text-sm font-semibold disabled:opacity-50"
                >
                  <X className="h-4 w-4" /> Refuser
                </button>
                <Button
                  onClick={() =>
                    moderate.mutate({
                      themeId: t.id,
                      action: 'approve',
                      reason: reasonByTheme[t.id] || undefined,
                    })
                  }
                  disabled={moderate.isPending}
                  className="min-h-[2.75rem] w-full sm:w-auto"
                >
                  <Check className="h-4 w-4" /> Publier
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
